import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreVertical, RefreshCw, Bot, Brain, User, CheckCircle2, AlertTriangle, XCircle, HelpCircle, Clock } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useParams } from 'react-router-dom';
import { useParserBotSuppliers } from '@/hooks/useParserBotSuppliers';
import { useBillParsing } from '@/hooks/useBillParsing';
import { BillActionModal } from './BillActionModal';
import { PartiallyParsedCard } from './PartiallyParsedCard';
import { formatDistanceToNow } from 'date-fns';
import { ProcessingQueueHeader } from './ProcessingQueueHeader';

export function ProcessingQueuePanel() {
  const { workspaceId } = useParams();
  const [selectedBills, setSelectedBills] = useState<string[]>([]);
  const [showActionModal, setShowActionModal] = useState(false);

  // Get bills in processing stage
  const { data: bills = [], isLoading } = useQuery({
    queryKey: ['bills', 'processing', workspaceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bills')
        .select('*')
        .eq('stage', 'processing')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!workspaceId
  });

  const { suppliers } = useParserBotSuppliers();
  const { reparseBill, triggerParse } = useBillParsing();

  const enabledSuppliers = suppliers.filter(s => s.is_enabled);

  const toggleBillSelection = (billId: string) => {
    setSelectedBills(prev =>
      prev.includes(billId) ? prev.filter(id => id !== billId) : [...prev, billId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedBills.length === bills.length) {
      setSelectedBills([]);
    } else {
      setSelectedBills(bills.map(b => b.id));
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle2 className="h-4 w-4 text-success" />;
      case 'partial':
        return <AlertTriangle className="h-4 w-4 text-warning" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-destructive" />;
      case 'unmatched_vendor':
        return <HelpCircle className="h-4 w-4 text-blue-500" />;
      case 'waiting_manual':
        return <Clock className="h-4 w-4 text-muted-foreground" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { label: string; variant: any }> = {
      success: { label: 'Parsed', variant: 'default' },
      partial: { label: 'Partially Parsed', variant: 'secondary' },
      failed: { label: 'Failed', variant: 'destructive' },
      unmatched_vendor: { label: 'Unmatched Vendor', variant: 'outline' },
      waiting_manual: { label: 'Waiting Manual', variant: 'secondary' },
      pending: { label: 'Pending', variant: 'outline' }
    };

    const config = variants[status] || variants.pending;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'parser_bot':
        return <Bot className="h-4 w-4 text-primary" />;
      case 'openai':
        return <Brain className="h-4 w-4 text-purple-500" />;
      case 'manual':
        return <User className="h-4 w-4 text-orange-500" />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center p-12">Loading...</div>;
  }

  // Calculate stats
  const stats = {
    total: bills.length,
    pending: bills.filter(b => b.parsing_status === 'pending').length,
    partial: bills.filter(b => b.parsing_status === 'partial').length,
    unmatched: bills.filter(b => b.parsing_status === 'unmatched_vendor').length,
    success: bills.filter(b => b.parsing_status === 'success').length
  };

  return (
    <div className="space-y-4">
      {/* Stats Header */}
      <ProcessingQueueHeader stats={stats} />
      {/* Bulk Actions Toolbar */}
      {selectedBills.length > 0 && (
        <Card className="bg-muted">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {selectedBills.length} bill{selectedBills.length > 1 ? 's' : ''} selected
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => setShowActionModal(true)}
                  disabled={!workspaceId}
                >
                  <Bot className="h-4 w-4 mr-2" />
                  Parse Selected
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSelectedBills([])}
                >
                  Clear Selection
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bills Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={selectedBills.length === bills.length && bills.length > 0}
                  onCheckedChange={toggleSelectAll}
                />
              </TableHead>
              <TableHead>Bill #</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bills.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No bills in processing queue
                </TableCell>
              </TableRow>
            ) : (
              bills.map((bill) => (
                <>
                  <TableRow key={bill.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedBills.includes(bill.id)}
                        onCheckedChange={() => toggleBillSelection(bill.id)}
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      {bill.file_name || `Bill-${bill.id.slice(0, 8)}`}
                    </TableCell>
                    <TableCell>
                      {bill.supplier_name ? (
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-success" />
                          <span>{bill.supplier_name}</span>
                        </div>
                      ) : (
                        <Badge variant="outline">Not Matched</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(bill.parsing_status)}
                        {getStatusBadge(bill.parsing_status)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {bill.parsing_method && (
                        <div className="flex items-center gap-2">
                          {getMethodIcon(bill.parsing_method)}
                          <span className="text-sm capitalize">
                            {bill.parsing_method.replace('_', ' ')}
                          </span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {bill.created_at && formatDistanceToNow(new Date(bill.created_at), { addSuffix: true })}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedBills([bill.id]);
                              setShowActionModal(true);
                            }}
                          >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Re-parse
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              reparseBill.mutate({
                                billId: bill.id,
                                method: 'parser_bot'
                              });
                            }}
                          >
                            <Bot className="h-4 w-4 mr-2" />
                            Re-parse (Parser Bot)
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              reparseBill.mutate({
                                billId: bill.id,
                                method: 'openai'
                              });
                            }}
                          >
                            <Brain className="h-4 w-4 mr-2" />
                            Re-parse (AI)
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              reparseBill.mutate({
                                billId: bill.id,
                                method: 'manual'
                              });
                            }}
                          >
                            <User className="h-4 w-4 mr-2" />
                            Send to Manual
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                  
                  {/* Show partial parsing details */}
                  {bill.parsing_status === 'partial' && bill.todo_fields && (
                    <TableRow>
                      <TableCell colSpan={7} className="bg-muted/50">
                        <PartiallyParsedCard bill={bill} />
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Action Modal */}
      <BillActionModal
        open={showActionModal}
        onClose={() => {
          setShowActionModal(false);
          setSelectedBills([]);
        }}
        billIds={selectedBills}
        workspaceId={workspaceId || ''}
        enabledSuppliers={enabledSuppliers.map(s => ({
          supplier_id: s.supplier_id,
          supplier_name: s.supplier_name
        }))}
      />
    </div>
  );
}
